import { Badge, Button, Popover, Image, Skeleton, Modal } from "antd"
import { useMemo, useState } from "react"
import { Dayjs } from "dayjs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCommentDots } from "@fortawesome/pro-regular-svg-icons/faCommentDots"
import { faPlay } from "@fortawesome/pro-solid-svg-icons/faPlay"
import { CalendarEventVisit, DayViewEvent, Timesheet, Visit, VisitAttachment, VisitWorkareaStatusRecord } from "../scheduleApi"
import { getVisitForEvent, getDayDateRange, mapDayViewEventToScheduledEvent } from "../scheduleModel"
import JobDrawer from "../JobDrawer/JobDrawer"
import { useGetVisitAttachmentsQuery } from "../scheduleQueries"

type Props = {
  events: DayViewEvent[]
  visits: Visit[]
  calendarEventVisits: CalendarEventVisit[]
  visitWorkareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
  date: Dayjs
}

type VisitAttachmentThumbnailsProps = {
  visitId: string
  onPreviewChange: (visible: boolean) => void
}

const videoExtensions = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v']

const isVideo = (attachment: VisitAttachment): boolean => {
  const path = attachment.path.toLowerCase()
  return videoExtensions.some(ext => path.endsWith(ext))
}

const VisitAttachmentThumbnails = ({ visitId, onPreviewChange }: VisitAttachmentThumbnailsProps) => {
  const { data: attachments, isLoading } = useGetVisitAttachmentsQuery(visitId)
  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState<VisitAttachment | null>(null)

  const attachmentsWithThumbnails = attachments?.filter(a => a.thumbnail) ?? []
  const skeletonCount = attachmentsWithThumbnails.length || 2

  const handleVideoClick = (attachment: VisitAttachment) => {
    setSelectedVideo(attachment)
    setVideoModalOpen(true)
    onPreviewChange(true)
  }

  const handleVideoModalClose = () => {
    setVideoModalOpen(false)
    onPreviewChange(false)
  }

  if (isLoading) {
    return (
      <div style={{ marginTop: '8px', display: 'flex', gap: '4px' }}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Skeleton.Image key={i} active style={{ width: 48, height: 48 }} />
        ))}
      </div>
    )
  }

  if (attachmentsWithThumbnails.length === 0) {
    return null
  }

  const images = attachmentsWithThumbnails.filter(a => !isVideo(a))
  const videos = attachmentsWithThumbnails.filter(a => isVideo(a))

  return (
    <>
      <div style={{ marginTop: '8px' }} onClick={e => e.stopPropagation()}>
        <Image.PreviewGroup
          preview={{
            onVisibleChange: onPreviewChange
          }}
        >
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {images.map(attachment => (
              <Image
                key={attachment.id}
                src={attachment.thumbnail!}
                alt={attachment.name}
                width={48}
                height={48}
                style={{
                  objectFit: 'cover',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                preview={{
                  src: attachment.url
                }}
                onClick={e => e.stopPropagation()}
              />
            ))}
            {videos.map(attachment => (
              <div
                key={attachment.id}
                style={{
                  position: 'relative',
                  width: 48,
                  height: 48,
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  handleVideoClick(attachment)
                }}
              >
                <img
                  src={attachment.thumbnail!}
                  alt={attachment.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '4px'
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '4px'
                  }}
                >
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FontAwesomeIcon icon={faPlay} style={{ fontSize: '10px', color: '#333', marginLeft: '1px' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      </div>
      <Modal
        open={videoModalOpen}
        onCancel={handleVideoModalClose}
        footer={null}
        width={800}
        centered
        destroyOnClose
      >
        {selectedVideo && (
          <video
            src={selectedVideo.url}
            controls
            autoPlay
            style={{ width: '100%', maxHeight: '70vh' }}
          />
        )}
      </Modal>
    </>
  )
}

const CrewLeadNotesPopover = ({ events, visits, calendarEventVisits, visitWorkareaStatuses, timesheets, date }: Props) => {
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  const selectedEvent = events.find(e => e.id === selectedEventId) ?? null
  const selectedEventData = selectedEvent ? {
    event: mapDayViewEventToScheduledEvent(selectedEvent),
    calendarEventVisits,
    visits,
    visitWorkareaStatuses,
    timesheets,
  } : null

  const eventsWithNotes = useMemo(() => {
    return events.reduce<{ event: DayViewEvent; visit: Visit }[]>((acc, event) => {
      const visit = getVisitForEvent(event, calendarEventVisits, visits, getDayDateRange(date))
      if (visit?.internalNotes) {
        acc.push({ event, visit })
      }
      return acc
    }, [])
  }, [events, calendarEventVisits, visits, date])

  const notesCount = eventsWithNotes.length

  const formatAddress = (event: DayViewEvent) => {
    const parts = [
      event.customerAddress1,
      event.customerAddress2,
      event.customerCity,
      event.customerState,
      event.customerZip
    ].filter(Boolean)
    return parts.join(', ') || 'No address'
  }

  const notesPopoverContent = (
    <div
      style={{ maxHeight: '400px', overflowY: 'auto', maxWidth: '350px' }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      {eventsWithNotes.map(({ event, visit }) => (
        <div
          key={event.id}
          style={{
            borderBottom: '1px solid #f0f0f0',
            padding: '12px 0'
          }}
        >
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
            {event.customerDisplayName || 'No customer'}
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            {event.jobName}
          </div>
          <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
            {formatAddress(event)}
          </div>
          <div
            style={{
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              padding: '8px',
              fontSize: '13px',
              color: '#333',
              whiteSpace: 'pre-wrap'
            }}
          >
            {visit.internalNotes}
            <VisitAttachmentThumbnails visitId={visit.id} onPreviewChange={setPreviewOpen} />
          </div>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, marginTop: '8px' }}
            onClick={(e) => {
              e.stopPropagation()
              setSelectedEventId(event.id)
              setDrawerOpen(true)
            }}
          >
            View Event
          </Button>
        </div>
      ))}
    </div>
  )

  if (notesCount === 0) {
    return null
  }

  return (
    <>
      <Popover
        content={notesPopoverContent}
        title={<div style={{ margin: '-5px -16px', padding: '5px 16px' }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>Notes & Photos</div>}
        trigger="click"
        open={popoverOpen}
        onOpenChange={(open) => {
          if (!open && previewOpen) return
          setPopoverOpen(open)
        }}
      >
        <Badge count={notesCount} size="small">
          <Button
            type="text"
            icon={<FontAwesomeIcon icon={faCommentDots} style={{ fontSize: '18px' }} />}
            onClick={(e) => {
              e.stopPropagation()
              setPopoverOpen(!popoverOpen)
            }}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: '#fff',
              border: '1px solid #d9d9d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </Badge>
      </Popover>
      <JobDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        eventData={selectedEventData}
        date={date}
        dateRange={getDayDateRange(date)}
      />
    </>
  )
}

export default CrewLeadNotesPopover
